/**
 * Presentational helpers for workspace artifacts (the Artifacts section). All keep the "open union"
 * rule: `source` / `content_type` are cast, not validated, so a newer service can send a value
 * outside the known set — every lookup falls back to a safe default rather than indexing a map
 * blindly (CLAUDE.md).
 */

/**
 * How an artifact should be previewed in-product. `table` renders CSV/TSV as a grid, `markdown`
 * renders `.md` as formatted prose, `html` renders in a sandboxed frame; `none` = no inline preview
 * (download only), and `pdf` pages are drawn with pdf.js. `docx`, `xlsx` and `pptx` are drawn on a
 * canvas by `@silurus/ooxml`; macro-enabled and legacy Office files stay `none`. `svg` is shown as an
 * image from a `data:` URL, `zip` as the tree of files inside the archive, and `mermaid` (`.mmd`) as the
 * diagram its source draws. `audio` (`.mp3`) and `video` (`.mp4`) play in the browser's own player.
 */
export type ArtifactPreviewKind =
  | 'image'
  | 'svg'
  | 'text'
  | 'markdown'
  | 'mermaid'
  | 'table'
  | 'html'
  | 'pdf'
  | 'docx'
  | 'xlsx'
  | 'pptx'
  | 'zip'
  | 'audio'
  | 'video'
  | 'none';

/** The Office formats the viewer draws with `@silurus/ooxml`. */
export type OfficePreviewKind = 'docx' | 'xlsx' | 'pptx';

/** Office files above this size skip the preview: parsing them would stall the tab for little benefit. */
export const OFFICE_PREVIEW_MAX_BYTES = 25 * 1024 * 1024;

/** An SVG is read whole as text to show it; past this size it is a drawing nobody previews. */
export const SVG_PREVIEW_MAX_BYTES = 5 * 1024 * 1024;

/** A zip is read whole to list it — Graph and S3 serve no ranges — so the cap bounds that download. */
export const ZIP_PREVIEW_MAX_BYTES = 50 * 1024 * 1024;

/** A Mermaid source is drawn whole, and one this large is not a diagram anyone reads on a screen. */
export const MERMAID_PREVIEW_MAX_BYTES = 1024 * 1024;

/**
 * A recording is read whole into the player: the content routes take a bearer, so a `<video>` cannot
 * stream them. This is the platform's transfer cap, so every file a source will hand over still plays.
 */
export const MEDIA_PREVIEW_MAX_BYTES = 100 * 1024 * 1024;

/**
 * The most bytes a preview of this kind reads, or null for the kinds that are not capped this way. A text
 * preview reads only the head of its file instead, so it needs no cap; the kinds here must read all of it.
 */
export function previewByteCap(kind: ArtifactPreviewKind): number | null {
  if (isOfficePreviewKind(kind)) return OFFICE_PREVIEW_MAX_BYTES;
  if (kind === 'svg') return SVG_PREVIEW_MAX_BYTES;
  if (kind === 'zip') return ZIP_PREVIEW_MAX_BYTES;
  if (kind === 'mermaid') return MERMAID_PREVIEW_MAX_BYTES;
  if (isMediaPreviewKind(kind)) return MEDIA_PREVIEW_MAX_BYTES;
  return null;
}

/** What a previewable file says when it is too big to draw: over the size cap, or over the renderer's limits. */
export const TOO_LARGE_TO_PREVIEW =
  'This file is too large to preview. Download it to open it.';

/** Whether a preview kind is one of the Office formats. */
export function isOfficePreviewKind(
  kind: ArtifactPreviewKind,
): kind is OfficePreviewKind {
  return kind === 'docx' || kind === 'xlsx' || kind === 'pptx';
}

/** Whether a preview kind plays in a player rather than being drawn. */
export function isMediaPreviewKind(kind: ArtifactPreviewKind): boolean {
  return kind === 'audio' || kind === 'video';
}

/**
 * Whether a file of this kind gets the Assistant in its viewer: every previewable kind except a recording,
 * which the Assistant can neither read nor edit.
 */
export function hasFileAssistant(kind: ArtifactPreviewKind): boolean {
  return kind !== 'none' && !isMediaPreviewKind(kind);
}

/**
 * Slides and workbooks are wider than they are tall, so their viewer opens wide instead of in the
 * portrait shape a page or a document wants.
 */
export function isLandscapePreviewKind(kind: ArtifactPreviewKind): boolean {
  return kind === 'pptx' || kind === 'xlsx';
}

const SOURCE_LABELS: Record<string, string> = {
  agent_generated: 'Agent',
  user_upload: 'Upload',
  deck_link: 'Deck',
};

/** Human label for an artifact source. Unknown sources fall back to a title-cased value. */
export function sourceLabel(source: string): string {
  const known = SOURCE_LABELS[source];
  if (known !== undefined) return known;
  const trimmed = source.trim();
  if (trimmed.length === 0) return 'File';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).replace(/_/g, ' ');
}

/**
 * Whether this caller may write to this artifact — save a new revision, restore an older one, or rename
 * it. The server decides for real (`canEditArtifact` in cti-workspace-service's `artifact-access.ts`);
 * this mirror exists so a control that would 403 on click is never rendered, and the two must not drift.
 *
 * A `viewer` is read-only always, even on their own file: being the author widens what a member can SEE,
 * it never makes them a writer. So a `workspace`-visible artifact is writable by an owner or an editor,
 * and a `restricted` one by an owner or by its author holding at least editor.
 *
 * Fail-closed on both open unions: an unresolved role (`null`) or a role this build does not know denies,
 * and any visibility other than the literal `workspace` is treated as the narrower `restricted`.
 */
export function canEditArtifact(
  artifact: { createdBy: string; visibility: string },
  viewer: { userId: string | null; role: string | null },
): boolean {
  if (!canAddArtifact(viewer.role)) return false;
  if (artifact.visibility === 'workspace') return true;
  return canManageArtifact(artifact, viewer);
}

/** The caller as the access predicates read them: who they are and their role in the workspace. */
export type ArtifactViewer = { userId: string | null; role: string | null };

/**
 * Whether this caller may DELETE this artifact or change who can see it. Narrower than
 * {@link canEditArtifact} on purpose, and the same narrowing the server applies (`canDeleteArtifact` /
 * `canChangeArtifactVisibility`): saving a new version of a shared file is normal editing work, but
 * throwing the file away — or widening who can read it — belongs to its author or a workspace owner.
 *
 * So: a workspace owner, or the artifact's author holding at least `editor`. A `viewer` never qualifies,
 * even on their own file, and an unresolved or unknown role denies.
 */
export function canManageArtifact(
  artifact: { createdBy: string },
  viewer: { userId: string | null; role: string | null },
): boolean {
  if (!canAddArtifact(viewer.role)) return false;
  return (
    viewer.role === 'owner' ||
    (viewer.userId !== null && viewer.userId === artifact.createdBy)
  );
}

/**
 * Whether this caller may put a NEW file into the workspace. There is no artifact yet, so there is no
 * author and no visibility to consult — only the workspace role, which must be `owner` or `editor`. Also
 * the "is a writer at all" first gate of the two predicates above.
 */
export function canAddArtifact(role: string | null): boolean {
  return role === 'owner' || role === 'editor';
}

/** File extension (lower-case, no dot) of a name, or empty when there is none. */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

const TEXT_EXTENSIONS = new Set(['txt', 'json', 'log', 'yaml', 'yml']);
const MARKDOWN_EXTENSIONS = new Set(['md', 'markdown', 'mdx']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);
const TABLE_EXTENSIONS = new Set(['csv', 'tsv']);
const HTML_EXTENSIONS = new Set(['html', 'htm']);
const MERMAID_EXTENSIONS = new Set(['mmd', 'mermaid']);
const ZIP_CONTENT_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
]);

/** OpenXML content types by the preview kind they map to. Macro-enabled variants are deliberately absent. */
const OFFICE_CONTENT_TYPES: Record<string, OfficePreviewKind> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
};
const OFFICE_EXTENSIONS = new Set<string>(['docx', 'xlsx', 'pptx']);

/** The MIME essence of a content type — lower-cased, with any `; charset=…`-style parameters and
 * surrounding whitespace stripped — so `text/csv; charset=utf-8` compares equal to `text/csv`. */
function mimeEssence(contentType: string): string {
  return contentType.toLowerCase().split(';', 1)[0]!.trim();
}

/**
 * Decide how to preview an artifact from its content type and filename together. CSV/TSV render as a
 * table and HTML in a sandboxed frame. Anything unrecognised, such as a legacy or macro-enabled Office
 * format or an unknown binary, returns `none` (the safe default: download only). The MIME parameters are
 * stripped first, and the filename extension is weighed alongside the type so a `.csv`/`.tsv` served as
 * `text/plain` still previews as a table (and the extension covers generic types like
 * `application/octet-stream`). Image,
 * HTML, table and markdown are matched before the generic text branch, which would otherwise swallow
 * them (a `.md` served as `text/plain` still renders as markdown, and `text/markdown` still renders as
 * markdown even with an unknown extension).
 * SVG is its own kind, checked before the raster `image/*` branch. It is only ever shown through an
 * `<img>` whose source is a `data:` URL: in image mode a browser runs no script in an SVG and fetches
 * nothing it references, and a `data:` URL cannot be opened as a page, while a `blob:` URL opened in a new
 * tab would run the file's script on the app's origin. A zip is listed, never extracted, and a Mermaid file
 * is drawn by the same strict renderer as a ```mermaid block. The generic zip type counts only for a name
 * with no extension: a Word, Excel or PowerPoint file is a zip package too, and so are formats kept
 * download-only, such as `.docm` or `.odt`, and some tools report any of them as a zip. PDF IS previewable too: `PdfDocumentView`
 * draws its pages with pdf.js and lays a text layer over
 * them, so selection, copy and Ctrl+F work; printing is the one thing the native viewer had that this
 * does not. The blob went to that native viewer in an `<iframe>` until 2026-09, when it turned out the
 * viewer is a plugin Chromium will not start inside a sandboxed frame — which is where the SPA runs as a
 * Teams tab.
 */
export function resolvePreviewKind(
  contentType: string,
  name: string,
): ArtifactPreviewKind {
  const type = mimeEssence(contentType);
  const ext = extensionOf(name);
  if (type === 'image/svg+xml' || ext === 'svg') return 'svg';
  if (ext === 'zip') return 'zip';
  if (MERMAID_EXTENSIONS.has(ext)) return 'mermaid';
  if (type === 'audio/mpeg' || ext === 'mp3') return 'audio';
  if (type === 'video/mp4' || ext === 'mp4') return 'video';
  if (type === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (OFFICE_EXTENSIONS.has(ext)) return ext as OfficePreviewKind;
  const officeKind = OFFICE_CONTENT_TYPES[type];
  if (officeKind !== undefined) return officeKind;
  if (type.startsWith('image/') || IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (type === 'text/html' || HTML_EXTENSIONS.has(ext)) return 'html';
  if (
    type === 'text/csv' ||
    type === 'text/tab-separated-values' ||
    TABLE_EXTENSIONS.has(ext)
  ) {
    return 'table';
  }
  if (
    type === 'text/markdown' ||
    type === 'text/x-markdown' ||
    MARKDOWN_EXTENSIONS.has(ext)
  ) {
    return 'markdown';
  }
  if (
    type.startsWith('text/') ||
    type === 'application/json' ||
    TEXT_EXTENSIONS.has(ext)
  ) {
    return 'text';
  }
  if (ZIP_CONTENT_TYPES.has(type) && ext === '') return 'zip';
  return 'none';
}

/** The field delimiter for a table artifact — tab for TSV, comma otherwise. */
export function delimiterFor(contentType: string, name: string): ',' | '\t' {
  if (mimeEssence(contentType) === 'text/tab-separated-values') return '\t';
  return extensionOf(name) === 'tsv' ? '\t' : ',';
}

/**
 * Parse delimiter-separated text into rows of fields. Handles quoted fields that contain the
 * delimiter, newlines, or escaped (doubled) quotes — the RFC-4180 cases — so a preview of real CSV
 * doesn't split mid-value. A trailing newline does not emit an empty final row.
 */
export function parseDelimited(input: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let sawField = false;
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    if (inQuotes) {
      if (char === '"') {
        if (input[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      sawField = true;
    } else if (char === delimiter) {
      row.push(field);
      field = '';
      sawField = true;
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      sawField = false;
    } else if (char !== '\r') {
      field += char;
      sawField = true;
    }
  }
  if (sawField || field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * A short type label for the list column, derived from the content type with an extension fallback.
 * Prefers the file extension (upper-cased) when present since it reads better than a long MIME string.
 */
export function typeLabel(contentType: string, name: string): string {
  const ext = extensionOf(name);
  if (ext.length > 0) return ext.toUpperCase();
  const type = contentType.trim();
  if (type.length === 0) return 'File';
  const slash = type.indexOf('/');
  const subtype = slash >= 0 ? type.slice(slash + 1) : type;
  return subtype.toUpperCase();
}

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/** Human-readable byte size (e.g. `1.4 MB`). Non-finite / negative sizes render as `—`. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < SIZE_UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${SIZE_UNITS[unitIndex]}`;
}
